import { Address, ExternalLink, IAvsAddress, IAvsResponse, IAvsResponseV2, IInvalidEmailError } from '@cart-checkout/checkout/data-access/interfaces';
import { AddressFormField, AddressFormType, DPV_CODE, SinglePageCards } from '@cart-checkout/checkout/data-access/enums';
import { BehaviorSubject, Observable, combineLatest, of, throwError } from 'rxjs';
import { Checkout, ISameDayDataList } from '@cart-checkout/data-access/interfaces';
import { EmitEvent, EventBusService, Events } from '@shared/utils/services';
import { Injectable, inject } from '@angular/core';
import { catchError, concatMap, tap } from 'rxjs/operators';

import { AddressEvaluatorService } from './address-evaluator.service';
import { AddressV2 } from '@shared/data-access/interfaces';
import { AvailabilityService, CheckoutAnalyticsService, CheckoutService } from '@cart-checkout/data-access/services';
import { AvsService } from './avs.service';
import { CHECKOUT_ERROR_MESSAGES } from '@cart-checkout/checkout/data-access/constants';
import { CheckoutErrorLogKeys } from '@cart-checkout/data-access/enums';
import { ErrorMessageFeedService } from './error-message-feed.service';
import { LoadingOverlayService } from 'libs/shared/data-access/services/src/lib/loading-overlay.service';
import { MatDialog } from '@angular/material/dialog';
import { SavedAddressService } from './saved-address.service';
import { SuggestedAddressModalComponent } from '../../../../components/src/lib/suggested-address-modal/suggested-address-modal.component';

@Injectable({
	providedIn: 'root'
})
export class CheckoutAddressService {
	private avsAvailableValue = true;
	private analyticErrorLogKey: CheckoutErrorLogKeys = CheckoutErrorLogKeys.singlePageBilling;
	private readonly avsAvailableUpdate: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
		true
	);
	private readonly shippingRestrictionMessage: BehaviorSubject<string> = new BehaviorSubject<string>(
		''
	);
	private readonly invalidEmailErrorSubject = new BehaviorSubject<IInvalidEmailError>(
		{} as IInvalidEmailError
	);

	public avsAvailable$: Observable<boolean> = this.avsAvailableUpdate.asObservable();
	public shippingRestrictionMessage$: Observable<string> = this.shippingRestrictionMessage.asObservable();
	public invalidEmailError$ = this.invalidEmailErrorSubject.asObservable();

	private get avsAvailable() {
		return this.avsAvailableValue;
	}

	private set avsAvailable(available: boolean) {
		this.avsAvailableValue = available;
		this.avsAvailableUpdate.next(available);
	}

	private set invalidEmailError(error: IInvalidEmailError) {
		this.invalidEmailErrorSubject.next(error);
	}

	private addressEvaluatorService = inject(AddressEvaluatorService);

	constructor(
		private checkoutService: CheckoutService,
		private avsService: AvsService,
		private checkoutAnalyticsService: CheckoutAnalyticsService,
		private errorMessageFeedService: ErrorMessageFeedService,
		private eventBusService: EventBusService,
		private availabilityService: AvailabilityService,
		private dialog: MatDialog,
		private loadingOverlayService: LoadingOverlayService,
		private savedAddressService: SavedAddressService
	) { }

	public setErrorLogKey(logKey: CheckoutErrorLogKeys): void {
		this.analyticErrorLogKey = logKey;
	}

	/**
	 * @description
	 * AVS Service call and response handling
	 *
	 * @param addressFormValue - Submitted form values
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @param checkout - Checkout object
	 * @param sddAddress - Informs if address is same day delivery
	 * @param shouldUseV2 - Determines how we should handle AVS response
	 * @param contactInfo - Needed in modal suggestion
	 * @param savedAddresses - User's saved addresses
	 */
	public avsServiceCall(
		addressFormValue: Address,
		formName: AddressFormType,
		checkout?: Checkout,
		sddAddress = false,
		shouldUseV2 = false,
		contactInfo?: any,
		sameShippingChecked?: boolean
	): Observable<void | IAvsResponse | IAvsResponseV2> {
		// Check if AVS is available to check the address against
		if (this.avsAvailable) {
			const serviceCall$: Observable<IAvsResponse | IAvsResponseV2> = shouldUseV2
				? this.avsService.verifyAddressV2$(
					addressFormValue,
					checkout ? checkout.cart.external_key : null,
					formName
				)
				: this.avsService.verifyAddress$(
					addressFormValue,
					checkout ? checkout.cart.external_key : null,
					formName
				);

			return serviceCall$.pipe(
				concatMap((avsAddress: IAvsResponse | IAvsResponseV2) =>
					// Do the AVS actions with the AVS Response
					this.doAvsActions(
						avsAddress,
						formName,
						shouldUseV2,
						contactInfo,
						sddAddress,
						addressFormValue,
						sameShippingChecked
					)
				),
				catchError((error: { dpvCode?: string, errorMessage?: string, message: string, name: string, rejectedFormField?: string }) => {
					/*
					 * ***** SINGLE CATCH BLOCK FOR ERROR HANDLING *****
					 * AVS call failed or #doAvsActions returned an error (runtime error or INTENTIONAL string error in case of AVS_DOWN)
					 *
					 * NOTE: #doAvsDownActions method should NOT / will NOT log the error, error logging is handled here
					 * because there may have been some other issue that occurred (e.g. runtime error). However, error messaging displayed to
					 * the user IS handled within the #doAvsDownActions method
					 */

					// Log the error
					this.checkoutAnalyticsService.generateLogError(
						this.analyticErrorLogKey,
						this.determineAvsFailureMessageForLogging(
							error.message,
							formName,
							sddAddress
						),
						error
					);

					// If #doAvsActions returned an INTENTIONAL string error, it would be typeof string so this indicates
					// an XHR failure or runtime error and the #doAvsActions logic might not be completely executed
					if (typeof error !== 'string' && error.message !== 'AVS_REJECT' && error.message !== 'DUPLICATE_ADDRESS') {
						// Clear any potential error messages that might have been pushed from #doAvsActions to prevent more than one message from displaying
						this.errorMessageFeedService.clear();

						// Treat the XHR/runtime error scenario as if the AVS service call failed and AVS is down
						this.doAvsDownActions(this.getErrorMessageLocation(formName));
					}

					// Return the error which could be an XHR failure, runtime error, or AVS action error (AVS_REJECT / AVS_DOWN)
					return throwError(error);
				})
			);
		} else {
			// AVS is down; Send address straight to checkout-engine without being verified (will go through AVS verification in checkout-engine again anyway)
			const avsFormattedAddress = this.constructAvsFormattedAddress(addressFormValue);

			// Set the AVS decision to ACCEPT because AVS is down, and we want to add the address to the checkout object
			const avsUnverifiedAddressResponse: IAvsResponse = {
				verifiedAddress: avsFormattedAddress,
				avsResult: {
					decision: 'ACCEPT',
					reviewNeeded: true
				},
				unverifiedAddress: avsFormattedAddress,
				referenceCode: '00000000000',
				originIdentifier: 'CHECKOUT-UI',
				formType: formName
			};

			// Do the AVS accept actions with an unverified AVS address (no need to check for reject / down in this scenario)
			return this.doAvsAcceptActions(avsUnverifiedAddressResponse);
		}
	}

	isInventoryContainsSDDEligibleItems(inventoryAvail: ISameDayDataList): boolean {
		return this.availabilityService.getSDDEligibleItemsCount(inventoryAvail) === 0;
	}

	/**
	 * @description
	 * Finalize addresses of checkout object to sync addresses to commerce.  After addresses are finalized,
	 * it calls GET /checkout to get an updated checkout object
	 */
	public finalizeAddresses(checkout: Checkout, location?: SinglePageCards): Observable<Checkout> {
		// Clear shipping restriction message
		this.shippingRestrictionMessage.next('');

		return this.checkoutService.finalizeAddressesWrapper$(checkout.checkout_key).pipe(
			catchError((error: any) => {
				return this.handleFinalizeAddressesError(error, checkout, location);
			})
		);
	}

	/**
	 * @description
	 * Error handler for finalizing addresses. The two major scenarios are HTTP status codes 431 and 451.
	 *
	 * @param error
	 * @param checkout
	 */
	public handleFinalizeAddressesError(
		error: any,
		checkout: Checkout,
		location?: SinglePageCards,
		shouldFinalize?: boolean,
		reverseBillAndShippingKillswitch?: boolean,
		address?: string
	): Observable<never> | Observable<Checkout> {
		if (error.status === 451) {
			/*
			 * 451 indicates that we are unable to ship to that address due to restrictions (e.g. Puerto Rico, P.O. Box, APO/FPO, Oversized LTL restricted to AK/HI).
			 * Need to display the message + a sidenav. Full shipping restriction.
			 */
			return this.handleShippingRestrictionError(error, location, reverseBillAndShippingKillswitch);
		} else if (error.status === 431) {
			/*
			 * 431 indicates that shipping address is restricted for some items in cart due to specific ship modes.  This is thrown so that
			 * the UI does a refresh of the checkout object to get updated ship modes, ship charges, and EDD. This is done after WCS is updated
			 * to add the address and the new ship modes + etc. are returned after the update.  Need to display the message and refresh checkout.
			 * Partial Shipping Restriction.
			 */
			return this.handleShipModeRestrictionError(error, checkout, location, shouldFinalize, reverseBillAndShippingKillswitch, address);
		} else {
			return this.handleGenericError(error, location, shouldFinalize);
		}
	}

	/**
	 * @description
	 * Handles shipping restriction errors (HTTP status code 451).
	 */
	private handleShippingRestrictionError(
		error: any,
		location?: SinglePageCards,
		reverseBillAndShippingKillswitch?: boolean
	): Observable<never> {
		const errorMessage = error.error.internal_error_description ? error.error.internal_error_description : error.error.message;
		this.checkoutAnalyticsService.generateLogError(this.analyticErrorLogKey,
			`SHIPPING RESTRICTION ERROR: ${errorMessage}, STATUS CODE: ${error.status}, LOCATION: ${location || SinglePageCards.BILLING}`,
			error);

		this.shippingRestrictionMessage.next(`${errorMessage}`);

		const externalLink = {
			path: 'sale-and-shipping-restrictions',
			title: 'View Shipping Restrictions'
		} as ExternalLink;

		this.errorMessageFeedService.push(errorMessage, null, externalLink, reverseBillAndShippingKillswitch ? SinglePageCards.SHIPPING : location);

		return throwError(error);
	}

	/**
	 * @description
	 * Handles ship mode restriction errors (HTTP status code 431).
	 */
	private handleShipModeRestrictionError(
		error: any,
		checkout: Checkout,
		location?: SinglePageCards,
		shouldFinalize?: boolean,
		reverseBillAndShippingKillswitch?: boolean,
		address?: string
	): Observable<Checkout> {
		this.checkoutAnalyticsService.generateLogError(this.analyticErrorLogKey,
			`SHIPMODE RESTRICTION ERROR: ${error.error.message}, STATUS CODE: ${error.status}, LOCATION: ${location || SinglePageCards.BILLING}`,
			error);

		const shouldProcessError = this.shouldProcessError(error.error.message, checkout, address);

		if (shouldProcessError) {
			// TODO: revisit design to see if this needs to be shown in billing or shipping
			this.errorMessageFeedService.push(
				error.error.message,
				null,
				null,
				location === SinglePageCards.BILLING && !reverseBillAndShippingKillswitch
					? SinglePageCards.BILLING_MESSAGE
					: SinglePageCards.SHIPPING_MESSAGE
			);
		}

		// Refresh checkout and have address cards display the new address
		return this.checkoutService.getCheckoutWrapper$(checkout.checkout_key)
			.pipe(concatMap((res) => {
				if (shouldFinalize) {
					return this.checkoutService.finalizeAddressesWrapper$(checkout.checkout_key);
				}
				return of(res);
			}));
	}

	/**
	 * @description
	 * Handles generic errors.
	 */
	private handleGenericError(
		error: any,
		location?: SinglePageCards,
		shouldFinalize?: boolean
	): Observable<never> {
		this.errorMessageFeedService.push(
			CHECKOUT_ERROR_MESSAGES.generalAddress,
			null,
			null,
			location === SinglePageCards.BILLING
				? SinglePageCards.BILLING_GENERAL
				: SinglePageCards.SHIPPING_GENERAL
		);

		this.checkoutAnalyticsService.generateLogError(
			this.analyticErrorLogKey,
			shouldFinalize ? 'Error Finalizing Addresses' : 'Error Adding Address',
			error
		);

		return throwError(error);
	}

	private handleInvalidEmailError({ error }: { error: IInvalidEmailError }): void {
		this.invalidEmailError = error;
	}

	private isInvalidEmailError({ error }: any): boolean {
		return (
			error?.status === 400 &&
			// eslint-disable-next-line no-prototype-builtins
			error?.hasOwnProperty('ui_messaging') &&
			error['ui_messaging'].identifier === 'DCSG-CHECKOUT-INVALID-EMAIL'
		);
	}

	/**
	 * @description
	 * adds addresses into the checkout object
	 */
	public addAddressToCheckoutObject(
		checkout: Checkout,
		contactFormValue: any,
		addressFormValue: any,
		formName: AddressFormType,
		sameShipping = true,
		shouldFinalize = false,
		cardLocation?: any,
		needSDDEligibilityCheck = false,
		reverseBillAndShippingKillswitch?: boolean
	): Observable<Checkout | [Checkout, ISameDayDataList]> {
		const prevCheckout = { ...checkout };
		return this.checkoutService
			.addAndFinalizeAddressWrapper$(
				checkout,
				sameShipping ? null : formName,
				contactFormValue,
				addressFormValue,
				shouldFinalize
			)
			.pipe(
				tap((response: Checkout) => {
					// Check for partial shipping restriction message
					// Note: Partial shipping restrictions occur as 200 status code from modern checkout (Legacy they're a 431 status)
					if (response && response.shipping_restriction_message && this.shouldProcessError(response.shipping_restriction_message, prevCheckout, addressFormValue.address)) {
						this.errorMessageFeedService.push(
							response.shipping_restriction_message,
							null,
							null,
							cardLocation === SinglePageCards.BILLING
								? SinglePageCards.BILLING_MESSAGE
								: SinglePageCards.SHIPPING_MESSAGE
						);
					}
				}),
				concatMap((response: Checkout) => {
					return needSDDEligibilityCheck
						? combineLatest([
							of(response),
							this.availabilityService.getSameDayAvailability$(
								checkout?.cart?.items,
								addressFormValue?.zipcode.split('-')[0]
							)
						])
						: of(response);
				}),
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				tap((x) => {
					this.eventBusService.emit(new EmitEvent(Events.AddressChanged));
				}),
				catchError((error) => {
					if (this.isInvalidEmailError(error)) {
						this.handleInvalidEmailError(error);
					}

					return this.handleFinalizeAddressesError(
						error,
						checkout,
						cardLocation,
						shouldFinalize,
						reverseBillAndShippingKillswitch,
						addressFormValue.address
					);
				})
			);
	}

	/**
	 * @description
	 * Does certain actions based on the AVS result decision (e.g. ACCEPT, REJECT, etc.).
	 * Uses DPV codes to decide on a more specific action when shouldUseV2 is true.
	 *
	 * @param avsAddress - Response from AVS V1 or V2 API
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @param shouldUseV2 - Determines which reponse we should handle
	 * @param contactInfo - Needed in modal suggestion
	 * @param sddAddress - Informs if address is same day delivery
	 * @param savedAddresses - User's saved addresses
	 * @param addressFormValue - Submitted form values
	 * @returns Observable<void | IAvsResponse | IAvsResponseV2>
	 * @private
	 */
	private doAvsActions(
		avsAddress: IAvsResponse | IAvsResponseV2,
		formName: AddressFormType,
		shouldUseV2 = false,
		contactInfo?: any,
		sddAddress?: boolean,
		addressFormValue?: Address,
		sameShippingChecked = undefined
	): Observable<void | IAvsResponse | IAvsResponseV2> {
		const messageLocation = this.getErrorMessageLocation(formName);

		// V2 uses DPV codes to handle more specific validation situations
		if (shouldUseV2) {
			const v2Response = avsAddress as IAvsResponseV2;
			const dpvCode: number = v2Response?.avsResult?.dpvCode;

			if (dpvCode === undefined) {
				return this.doAvsDownActions(messageLocation);
			}

			// Always ensure same zip code for SDD
			let currAddress = v2Response.unverifiedAddress;
			const verifiedAddress = v2Response.verifiedAddress;
			if (
				sddAddress
				&& currAddress
				&& verifiedAddress
				&& currAddress.zipCode.split('-')[0] !== verifiedAddress.zipCode.split('-')[0]
			) {
				return this.doAvsRejectActions(formName, messageLocation, DPV_CODE.DISTINCT_FORM_FIELD_ERROR, AddressFormField.SDDZIPCODE);
			}

			if (dpvCode === DPV_CODE.INVALID_ADDRESS && formName !== AddressFormType.NEW_SAVED) {
				currAddress = v2Response.unverifiedAddress;
				const names = {};
				names['firstName'] = contactInfo.first_name;
				names['lastName'] = contactInfo.last_name;
				this.loadingOverlayService.hide();
				const dialogRef = this.dialog.open(SuggestedAddressModalComponent, {
					data: { avsResponse: v2Response, currentAddress: currAddress, names: names }
				});

				this.checkoutAnalyticsService.triggerCheckoutActionEvent('AvsV2:SuggestedAddress:ModalDisplayed');

				return dialogRef.afterClosed().pipe(concatMap(
					(acceptedSuggested) => {
						if (acceptedSuggested) {
							return this.doAvsAcceptActions(v2Response);
						} else {
							return this.doAvsRejectActions(formName, messageLocation, dpvCode);
						}
					})
				);
			}

			if (dpvCode === DPV_CODE.PERFECT_ADDRESS) {
				// If we are adding a primary address, remove the primary address from the check to prevent checking itself
				// Here we check if the address is already saved (same first name, last name, address, city, state, zip)
				const addressAlreadyExists = this.savedAddressService.savedAddressesWithoutPrimary?.some((savedAddress: AddressV2) => this.isVerifiedSameAsSavedAddress(v2Response.verifiedAddress, addressFormValue, savedAddress, sameShippingChecked));
				if (!addressAlreadyExists) {
					return this.doAvsAcceptActions(v2Response);
				} else {
					// Once verified, If the address is already saved, we should reject the duplicate with a custom message
					const errorMessage = CHECKOUT_ERROR_MESSAGES.duplicateAddress;
					this.errorMessageFeedService.push(
						errorMessage,
						null,
						null,
						messageLocation
					);

					return throwError(
						{
							name: `${formName}_DUPLICATE_ADDRESS`,
							message: 'DUPLICATE_ADDRESS',
							errorMessage,
							dpvCode: null,
							formField: null
						});
				}
			} else {
				return this.doAvsRejectActions(formName, messageLocation, dpvCode);
			}
		}

		// V1 validation and rules
		// Check the AVS result decision
		if (avsAddress?.avsResult?.decision === 'ACCEPT') {
			return this.doAvsAcceptActions(avsAddress);
		} else if (avsAddress?.avsResult?.decision === 'REJECT') {
			// REJECT actions
			return this.doAvsRejectActions(formName, messageLocation);

		} else {
			// Everything broke and the world is on fire
			return this.doAvsDownActions(messageLocation);
		}
	}

	/**
	 * @description
	 * Compares the verified address with the saved address to determine if they are the same.
	 * @param verifiedAddres - Address returned from AVS service
	 * @param addressForm - Address form values (for first name, last name)
	 * @param savedAddress - Address saved in the user's account
	 * @returns boolean
	 * */
	public isVerifiedSameAsSavedAddress(verifiedAddres: IAvsAddress, addressForm: Address, savedAddress: AddressV2, sameShippingChecked: boolean): boolean {
		const verified = {
			firstName: addressForm.first_name,
			lastName: addressForm.last_name,
			address: verifiedAddres.addressLines[1] ? `${verifiedAddres.addressLines[0]} ${verifiedAddres.addressLines[1]}` : verifiedAddres.addressLines[0],
			city: verifiedAddres.city,
			state: verifiedAddres.state,
			zipcode: verifiedAddres.zipCode
		};

		const saved = {
			firstName: savedAddress.firstName,
			lastName: savedAddress.lastName,
			address: savedAddress.addressLine2 ? `${savedAddress.addressLine1} ${savedAddress.addressLine2}` : savedAddress.addressLine1,
			city: savedAddress.city,
			state: savedAddress.state,
			zipcode: savedAddress.zip
		};

		for (const key of Object.keys(verified)) {
			if (verified[key].toLowerCase() !== saved[key].toLowerCase()) {
				return false;
			}
		}
		// If same sameShippingChecked is not undefined, we need to check if the sameShippingChecked is the same as the saved address
		if (sameShippingChecked !== undefined && typeof sameShippingChecked === 'boolean') {
			return !sameShippingChecked;
		}
		return true;
	}

	/**
	 * @description
	 * Executed when the AVS service responds with ACCEPT.
	 * @param avsResponse - Object returned from AVS V1 or V2 API
	 * @returns Observable<IAvsResponse | IAvsResponseV2>
	 * @private
	 */
	private doAvsAcceptActions(
		avsResponse: IAvsResponse | IAvsResponseV2
	): Observable<IAvsResponse | IAvsResponseV2> {
		return of(avsResponse);
	}

	/**
	 * @description
	 * Executed when the AVS service responds with REJECT.
	 *
	 * NOTE: This method will NOT log an error message, that is handled within #doAvsActions. This method
	 * is only responsible for returning what type of error occurred and the error message displayed
	 * to the user
	 *
	 * @param formName - AddressFormType - billing, shipping or newSavedAddress
	 * @param messageLocation - Determines which checkout card will display the rejection error
	 * @param dpvCode - Code returned from AVS service that informs us what went wrong
	 * @param rejectedFormField - Specific form field that caused the rejection
	 * @returns Observable<void>
	 * @private
	 */
	private doAvsRejectActions(
		formName: AddressFormType,
		messageLocation: SinglePageCards,
		dpvCode?: number,
		rejectedFormField?: AddressFormField,
	): Observable<void> {
		// Make error mesaage display dynamically based on dpv codes
		let errorMessage = '';
		switch (dpvCode) {
			case DPV_CODE.NO_DPV:
			case DPV_CODE.INVALID_ADDRESS:
				errorMessage = CHECKOUT_ERROR_MESSAGES.avsRejectedDPVCode0And2;
				break;

			case DPV_CODE.INVALID_APT_NUMBER:
				errorMessage = CHECKOUT_ERROR_MESSAGES.avsRejectedDPVCode3;
				break;

			case DPV_CODE.MISSING_APT_NUMBER:
				errorMessage = CHECKOUT_ERROR_MESSAGES.avsRejectedDPVCode4;
				break;

			case DPV_CODE.DISTINCT_FORM_FIELD_ERROR:
				errorMessage = this.getAvsFormFieldError(rejectedFormField);
				break;

			default:
				errorMessage = this.getGenericErrorMessage(formName);
		}

		// Send the error message through the error feed service
		this.errorMessageFeedService.push(
			errorMessage,
			null,
			null,
			messageLocation
		);

		if (dpvCode !== undefined) {
			this.checkoutAnalyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', { event: 'AvsV2:RejectionMessageDisplayed', value: `DPV Code: ${dpvCode}, ${errorMessage}` });
		}

		// Return an error
		return throwError({ name: `${formName}_AVS`, message: 'AVS_REJECT', errorMessage, dpvCode, rejectedFormField });
	}

	/**
	 * @description
	 * Executed when the AVS service does NOT respond with ACCEPT or REJECT or there is a runtime error performing AVS actions
	 *
	 * NOTE: This method will NOT log an error message, that is handled within #doAvsActions. This method
	 * is only responsible for returning what type of error occurred and the error message displayed
	 * to the user
	 *
	 * @private
	 */
	private doAvsDownActions(messageLocation?: SinglePageCards): Observable<void> {
		// Send the error message through the error feed service
		this.errorMessageFeedService.push(
			CHECKOUT_ERROR_MESSAGES.avsDown,
			null,
			null,
			messageLocation
		);

		// AVS is down, set the available flag to false
		this.avsAvailable = false;

		// Return an error
		return throwError('AVS_DOWN');
	}

	private constructAvsFormattedAddress(incomingAddress: any): IAvsAddress {
		const { address, address2, zipcode, ...remainingAddressDetails } = incomingAddress;
		return {
			addressLines: address2 === '' ? [address] : [address, address2],
			zipCode: zipcode,
			...remainingAddressDetails
		};
	}

	private determineAvsFailureMessageForLogging(
		error: string,
		formType: string,
		sddAddress?: boolean
	): string {
		if (error === 'AVS_REJECT') {
			let rejectionText = `AVS rejected the ${formType} address`;

			if (sddAddress) {
				rejectionText += ' during same day checkout process';
			}
			return rejectionText;
		} else if (error === 'AVS_DOWN') {
			return `An AVS error occurred for the ${formType} address`;
		} else if (error === 'DUPLICATE_ADDRESS') {
			return `User attempted duplicate address for the ${formType} address`;
		} else {
			return `A runtime or XHR error occurred for the ${formType} address: Error: ${error}`;
		}
	}

	private getGenericErrorMessage(formName): string {
		switch (formName) {
			case AddressFormType.SHIPPING:
				return CHECKOUT_ERROR_MESSAGES.avsShippingRejected;

			case AddressFormType.BILLING:
				return CHECKOUT_ERROR_MESSAGES.avsBillingRejected;

			case AddressFormType.NEW_SAVED:
				return CHECKOUT_ERROR_MESSAGES.avsSavedRejected;

			default:
				return CHECKOUT_ERROR_MESSAGES.general;
		}
	}

	private getAvsFormFieldError(formField: AddressFormField): string {
		// Change as needed to accommodate additional form field errors
		return formField === AddressFormField.SDDZIPCODE ? CHECKOUT_ERROR_MESSAGES.avsRejectedSDDZip : this.getGenericErrorMessage(null);
	}

	private getErrorMessageLocation(formName: AddressFormType): SinglePageCards {
		switch (formName) {
			case AddressFormType.SHIPPING:
				return SinglePageCards.SHIPPING;

			case AddressFormType.BILLING:
				return SinglePageCards.BILLING;

			case AddressFormType.NEW_SAVED:
			case AddressFormType.BILLING_NEW_SAVED:
			case AddressFormType.SHIPPING_NEW_SAVED:
				return SinglePageCards.NEW_SAVED;

			default:
				return SinglePageCards.GENERAL;
		}
	}

	private shouldProcessError(message: string, prevCheckout: Checkout, address: string): boolean {
		const STANDARD_SHIPPING_MESSAGE =
			'Due to shipping restrictions, the selected delivery method is not available for the shipping address provided. The delivery method has been updated.';

		const hasStandardShipping = prevCheckout.shipping.selected.groups
			.some(group => group.mode.description === 'Standard');

		const isPOBOX = Boolean(this.addressEvaluatorService.evaluateForPOBox(address));

		return !(hasStandardShipping && message === STANDARD_SHIPPING_MESSAGE && isPOBOX);
	}
}
